import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { ASSETTRANSFER_API, DEPARTMENT_API, ASSET_API, ASSETSTATUS_API } from '@app/utils/apiUrl';
import { ASSET_STATUS_NUMBER } from '@app/store/constants';
import { Post, Get, Put } from '@app/utils/httpProvider';
import { toast } from 'react-toastify';
import Select from 'react-select';
import {
  acceptDoc,
  acceptImage,
  messageError,
  messageToast,
  status200,
} from '../../utils/define';

const AssetTransfer = () => {
  const location = useLocation();
  const history = useHistory();
  const assetId = location.state?.id;
  const asset = location.state?.asset;
  const fromDepartmentName = location.state?.departmentName;

  const todayStr = new Date().toISOString().split("T")[0];

  const initialFormData = {
    assetId: assetId || '',
    fromDepartmentId: '',
    toDepartmentId: '',
    requestDate: '',
    requestedBy: '',
    receivedBy: '',
    approvedBy: '',
    approvedDate: '',
    transferDate: '',
    status: 'Chờ phê duyệt',
    targetStatusId: ASSET_STATUS_NUMBER.IN_USE
  };

  const [formData, setFormData] = useState(initialFormData);
  const [departments, setDepartments] = useState([]);
  const [assetStatuses, setAssetStatuses] = useState([]);

  // 1. Khởi tạo state quản lý lỗi
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const responseData = await Get(`${DEPARTMENT_API.GET_ALL}`);
        setDepartments(responseData);
      } catch (error) {
        console.error('Không thể kết nối đến API:', error);
        setDepartments([]); // Luôn set mảng rỗng khi lỗi để tránh crash
      }
    };

    const fetchStatuses = async () => {
      try {
        const responseData = await Get(`${ASSETSTATUS_API.GET_PAGING}`, null, { PageIndex: 1, PageSize: 10000 });
        const allowedCodes = ['NEW', 'IN_USE', 'BROKEN'];
        const filtered = (responseData?.data || []).filter(status => allowedCodes.includes(status.statusCode));
        setAssetStatuses(filtered);
      } catch (error) {
        console.error('Không thể kết nối đến API:', error);
        setAssetStatuses([]);
      }
    };

    fetchDepartments();
    fetchStatuses();
  }, []);

  // 2. Hàm validation form
  const validateForm = () => {
    const newErrors = {};

    // Validate Người đề nghị
    if (!formData.requestedBy || !formData.requestedBy.trim()) {
      newErrors.requestedBy = "Vui lòng nhập Người đề nghị.";
    }

    // Validate Người nhận
    if (!formData.receivedBy || !formData.receivedBy.trim()) {
      newErrors.receivedBy = "Vui lòng nhập Người nhận.";
    }

    // Validate Nơi đến
    if (!formData.toDepartmentId) {
      newErrors.toDepartmentId = "Vui lòng chọn Nơi đến.";
    } else if (Number(formData.fromDepartmentId) === Number(formData.toDepartmentId)) {
      // Bắt luôn lỗi trùng nơi đi - nơi đến vào đây
      newErrors.toDepartmentId = "Nơi đến không được trùng với Nơi đi.";
    }

    // Validate Trạng thái sau điều chuyển
    if (!formData.targetStatusId) {
      newErrors.targetStatusId = "Vui lòng chọn Trạng thái sau điều chuyển.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Trả về true nếu không có key lỗi nào
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    const val = (name.includes('Id') && !isNaN(value) && value !== '')
      ? Number(value)
      : value;

    setFormData({ ...formData, [name]: val });

    // 3. Xóa thông báo lỗi khi người dùng bắt đầu nhập lại/chọn lại
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 4. Gọi validate, nếu false thì dừng submit
    if (!validateForm()) {
      return;
    }

    const payload = {
      ...formData,
      assetId: asset?.id || formData.assetId,
      fromDepartmentId: Number(formData.fromDepartmentId),
      toDepartmentId: Number(formData.toDepartmentId)
    };
    console.log('Dữ liệu form:', payload);
    try {
      await Post(`${ASSETTRANSFER_API.ADD_ITEM}`, null, payload);
      try {
        const payload = {
          id: asset?.id,
          fieldName: 'AssetStatusId',
          value: ASSET_STATUS_NUMBER.AWAIT
        };

        await Put(`${ASSET_API.UPDATE_FIELD}`, null, payload);
      } catch (error) {
        console.error('Lỗi update AssetStatusId:', error);
        throw error;
      }
      toast.success(messageToast.messageToastUpdateSuccess);
    } catch (error) {
      console.error("Lỗi khi gọi API:", error);
      toast.error(messageToast.messageToastUpdateError);
    }
    history.goBack();
  };

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setFormData((prev) => ({
      ...prev,
      requestDate: today
    }));
  }, []);

  useEffect(() => {
    if (asset) {
      setFormData((prev) => ({
        ...prev,
        assetId: asset.id,
        fromDepartmentId: asset.currentDepartmentId,
        targetStatusId: ASSET_STATUS_NUMBER.IN_USE
      }));
    }
  }, [asset]);

  const resetForm = () => {
    const today = new Date().toISOString().split("T")[0];

    setFormData({
      ...initialFormData,
      requestDate: today,
      receivedBy: '',
      assetId: asset?.id,
      fromDepartmentId: asset?.currentDepartmentId,
      targetStatusId: ASSET_STATUS_NUMBER.IN_USE
    });
    // Xóa hết lỗi khi làm mới
    setErrors({});
  };

  return (
    <div className="container-fluid">
      <div className="card shadow-sm border-0" style={{ borderRadius: '4px', overflow: 'hidden' }}>
        <div className="card-header bg-secondary text-white text-center py-2.5">
          <h5 className="mb-0 fw-bold">PHIẾU ĐIỀU CHUYỂN TÀI SẢN</h5>
        </div>

        <div className="card-body p-4">
          <form onSubmit={handleSubmit}>
            <div className="row">

              {/* Cột trái */}
              <div className="col-12 col-md-6 mb-3">
                <label className="font-weight-bold mb-1">
                  1. Người đề nghị <span className="text-danger">*</span>
                </label>
                <input
                  // 5. Thêm class is-invalid
                  className={`form-control ${errors.requestedBy ? 'is-invalid' : ''}`}
                  name="requestedBy"
                  value={formData.requestedBy}
                  onChange={handleChange}
                  placeholder="Nhập họ tên..."
                />
                {/* 6. Hiển thị text lỗi */}
                {errors.requestedBy && <div className="text-danger mt-1" style={{ fontSize: '0.875em' }}>{errors.requestedBy}</div>}
              </div>

              <div className="col-12 col-md-6 mb-3">
                <label className="font-weight-bold mb-1">2. Ngày đề nghị</label>
                <input
                  type="date"
                  className="form-control"
                  name="requestDate"
                  value={formData.requestDate}
                  readOnly
                />
              </div>

              <div className="col-12 col-md-6 mb-3">
                <label className="font-weight-bold mb-1">3. Mã tài sản</label>
                <input className="form-control" name="assetCode"
                  value={asset?.assetCode || ''} readOnly />
              </div>

              <div className="col-12 col-md-6 mb-3">
                <label className="font-weight-bold mb-1">4. Thời gian chuyển</label>
                <input type="date" className="form-control"
                  name="transferDate" value={formData.transferDate}
                  min={todayStr}
                  onChange={handleChange}
                  onClick={(e) => {
                    if (e.target.showPicker) {
                      e.target.showPicker();
                    }
                  }}
                />
              </div>

              <div className="col-12 col-md-6 mb-3">
                <label className="font-weight-bold mb-1">5. Nơi đi</label>
                <input
                  className="form-control"
                  value={fromDepartmentName || ''}
                  readOnly
                />
              </div>

              <div className="col-12 col-md-6 mb-3">
                <label className="font-weight-bold mb-1">
                  6. Người nhận <span className="text-danger">*</span>
                </label>
                <input
                  className={`form-control ${errors.receivedBy ? 'is-invalid' : ''}`}
                  name="receivedBy"
                  value={formData.receivedBy}
                  onChange={handleChange}
                  placeholder="Nhập họ tên người nhận..."
                />
                {errors.receivedBy && <div className="text-danger mt-1" style={{ fontSize: '0.875em' }}>{errors.receivedBy}</div>}
              </div>

              {/* Cột phải */}
              <div className="col-12 col-md-6 mb-3">
                <label className="font-weight-bold mb-1">
                  7. Nơi đến <span className="text-danger">*</span>
                </label>
                <Select
                  options={departments.map((dept) => ({ value: dept.id, label: dept.departmentName }))}
                  value={departments.map((dept) => ({ value: dept.id, label: dept.departmentName })).find((opt) => Number(opt.value) === Number(formData.toDepartmentId)) || null}
                  onChange={(selectedOption) => {
                    const val = selectedOption ? Number(selectedOption.value) : '';
                    setFormData((prev) => ({ ...prev, toDepartmentId: val }));
                    if (errors.toDepartmentId) {
                      setErrors((prev) => ({ ...prev, toDepartmentId: null }));
                    }
                  }}
                  placeholder="-- Chọn phòng ban đến --"
                  isSearchable={true}
                  isClearable={true}
                  noOptionsMessage={() => "Không tìm thấy phòng ban"}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      minHeight: '38px',
                      borderRadius: '4px',
                      borderColor: errors.toDepartmentId ? '#dc3545' : state.isFocused ? '#86b7fe' : '#ced4da',
                      boxShadow: 'none'
                    }),
                    menu: (base) => ({ ...base, zIndex: 9999 })
                  }}
                />
                {errors.toDepartmentId && <div className="text-danger mt-1" style={{ fontSize: '0.875em' }}>{errors.toDepartmentId}</div>}
              </div>

              {/* 8. Trạng thái sau điều chuyển */}
              <div className="col-12 col-md-6 mb-3">
                <label className="font-weight-bold mb-1">
                  8. Trạng thái sau điều chuyển <span className="text-danger">*</span>
                </label>
                <Select
                  options={assetStatuses.map((status) => ({ value: status.id, label: status.statusName }))}
                  value={assetStatuses.map((status) => ({ value: status.id, label: status.statusName })).find((opt) => Number(opt.value) === Number(formData.targetStatusId)) || null}
                  onChange={(selectedOption) => {
                    const val = selectedOption ? Number(selectedOption.value) : '';
                    setFormData((prev) => ({ ...prev, targetStatusId: val }));
                    if (errors.targetStatusId) {
                      setErrors((prev) => ({ ...prev, targetStatusId: null }));
                    }
                  }}
                  placeholder="-- Chọn trạng thái sau điều chuyển --"
                  isSearchable={true}
                  isClearable={true}
                  noOptionsMessage={() => "Không tìm thấy trạng thái"}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      minHeight: '38px',
                      borderRadius: '4px',
                      borderColor: errors.targetStatusId ? '#dc3545' : state.isFocused ? '#86b7fe' : '#ced4da',
                      boxShadow: 'none'
                    }),
                    menu: (base) => ({ ...base, zIndex: 9999 })
                  }}
                />
                {errors.targetStatusId && <div className="text-danger mt-1" style={{ fontSize: '0.875em' }}>{errors.targetStatusId}</div>}
              </div>

              {/* Button Section */}
              <div className="col-12 mt-4 text-center">
                <button type="submit" className="btn btn-primary px-3 shadow-sm mr-3">
                  Lưu phiếu
                </button>
                <button type="button"
                  className="btn btn-secondary px-3"
                  onClick={resetForm}>
                  Làm mới
                </button>
              </div>

            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AssetTransfer;